<template>
  <v-container fluid>
    <PageHeader
      title="Dashboard"
      icon="mdi-view-dashboard-variant"
    />
    <v-tabs
      v-model="tab"
    >
      <v-tab
        value="map"
        text="Map"
      />
      <v-tab
        value="analytics"
        text="Analytics"
      />
    </v-tabs>
    <v-row>
      <v-col cols="4" />
      <v-col>
        <v-row
          dense
          class="filter-boxes"
        >
          <v-col cols="6">
            <v-menu
              v-model="dateField"
              :close-on-content-click="false"
              :offset="40"
              transition="scale-transition"
              min-width="auto"
            >
              <template #activator="{ props }">
                <v-text-field
                  v-model="dateRangeText"
                  label="Date Filter"
                  prepend-icon="mdi-calendar"
                  readonly
                  density="compact"
                  v-bind="props"
                  clearable
                  v-on="props"
                  @click:clear="dateTimeFullText = null"
                />
              </template>
              <v-date-picker
                v-model="dateTimeFullText"
                multiple="range"
                show-adjacent-months
              />
            </v-menu>
          </v-col>
          <v-col cols="6">
            <v-select
              v-model="selectedSightingTypes"
              :items="enums.sightingTypes"
              prepend-icon="mdi-binoculars"
              label="Sighting Type"
              name="sighting_type"
              density="compact"
              multiple
              clearable
            >
              <template #selection="{ item, index }">
                <span v-if="index < maxSelectDisplay">{{ item.title }} &nbsp;</span>
                <span
                  v-if="index === maxSelectDisplay"
                  class="grey-text text-caption"
                >(+{{ selectedSightingTypes?.length - maxSelectDisplay }} more)</span>
              </template>
            </v-select>
          </v-col>
        </v-row>
        <v-row
          dense
          class="filter-boxes-2"
        >
          <v-col cols="6">
            <v-select
              v-model="selectedSpecies"
              :items="Object.values(enums.sightingSpecies)"
              prepend-icon="mdi-dolphin"
              label="Species"
              name="species"
              density="compact"
              multiple
              clearable
            >
              <template #selection="{ item, index }">
                <span v-if="index < maxSelectDisplay">{{ item.title }} &nbsp;</span>
                <span
                  v-if="index === maxSelectDisplay"
                  class="grey-text text-caption"
                >(+{{ selectedSpecies?.length - maxSelectDisplay }} more)</span>
              </template>
            </v-select>
          </v-col>
          <v-col cols="6">
            <v-select
              v-model="selectedBehaviours"
              :items="Object.values(enums.sightingBehaviour)"
              prepend-icon="mdi-waves"
              label="Behaviour"
              name="behaviour"
              density="compact"
              multiple
              clearable
            >
              <template #selection="{ item, index }">
                <span v-if="index < maxSelectDisplay">{{ item.title }} &nbsp;</span>
                <span
                  v-if="index === maxSelectDisplay"
                  class="grey-text text-caption"
                >(+{{ selectedBehaviours?.length - maxSelectDisplay }} more)</span>
              </template>
            </v-select>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row v-show="tab === 'map'">
      <v-col cols="4">
        <div
          class="font-weight-bold pt-4 text-subtitle-1"
        >
          Sightings by Species
        </div>
        <v-divider class="divider" />

        <v-table density="compact">
          <template #default>
            <thead>
              <tr>
                <th class="text-left">
                  Species
                </th>
                <th class="text-left">
                  Sightings
                </th>
              </tr>
            </thead>
            <tbody>
              <template
                v-for="bucket in speciesBuckets"
                :key="bucket.label"
              >
                <tr
                  :class="{ 'bucket-row': bucket.codes.length > 1 }"
                  @click="toggleBucket(bucket)"
                >
                  <td class="col-7">
                    <v-icon
                      v-if="bucket.codes.length > 1"
                      size="small"
                      class="mr-1"
                    >
                      {{ expandedBuckets[bucket.label] ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                    </v-icon>
                    {{ bucket.label }}
                  </td>
                  <td>{{ bucket.count }}</td>
                </tr>
                <tr
                  v-for="spec in (expandedBuckets[bucket.label] ? bucket.codes : [])"
                  :key="bucket.label + '-' + spec"
                  class="bucket-child-row"
                >
                  <td class="col-7 pl-8">
                    {{ spec }}
                  </td>
                  <td>{{ countSpecies(spec) }}</td>
                </tr>
              </template>
            </tbody>
          </template>
        </v-table>
        <div
          class="font-weight-bold pt-4 text-subtitle-1"
        >
          Sightings by Year
        </div>
        <v-divider class="divider" />
        <div class="year-chart">
          <div
            v-for="entry in yearChart"
            :key="entry.year"
            class="year-row"
          >
            <span class="year-label">{{ entry.year }}</span>
            <div class="year-bar-wrap">
              <div
                class="year-bar"
                :style="{ width: entry.pct + '%' }"
              />
            </div>
            <span class="year-count">{{ entry.count }}</span>
          </div>
        </div>
        <div
          class="font-weight-bold pt-4 text-subtitle-1"
        >
          Total Sightings Shown: {{ filteredItems?.length }}
        </div>
      </v-col>
      <v-col>
        <v-row dense>
          <div
            id="dashboard-map"
            class="map-class"
          />
        </v-row>
      </v-col>
    </v-row>
    <Analytics
      v-if="tab === 'analytics'"
      :filters="analyticsFilters"
    />
    <sighting-details :view-only="true" />
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import PageHeader from "@/components/PageHeader";
import enums from "@/helpers/enums";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import SightingDetails from "@/components/sighting/Details.vue";
import Analytics from "@/components/dashboard/Analytics.vue";
import speciesHelper from "@/helpers/speciesBuckets";

export default {
  components: {
    PageHeader,
    SightingDetails,
    Analytics,
  },
  data() {
    return {
      tab: "map",
      maxSelectDisplay: 1,
      query: "",
      dateTimeFullText: null,
      dateField: false,
      options: {},
      selectedSpecies: [],
      selectedBehaviours: [],
      selectedSightingTypes: [],
      accessToken: process.env.VUE_APP_MAPBOX_TOKEN,
      mapStyle: "mapbox://styles/mapbox/outdoors-v12",
      mapCenter: [-128.9110, 54.6057],
      zoom: 4.15,
      map: null,
      markers: [],
      expandedBuckets: {},
    };
  },
  computed: {
    ...mapGetters({
      loading: "dashboard/loading",
      searchOptions: "dashboard/searchOptions",
      searchQuery: "dashboard/searchQuery",
      searchDate: "dashboard/searchDate",
      searchSpecies: "dashboard/searchSpecies",
      searchBehaviour: "dashboard/searchBehaviour",
      searchSightingType: "dashboard/searchSightingType",
      items: "dashboard/items",
    }),
    analyticsFilters() {
      return {
        date: this.dateTimeFullText,
        species: this.selectedSpecies,
        sightingType: this.selectedSightingTypes,
        behaviour: this.selectedBehaviours,
      };
    },
    dateRangeText() {
      if (this.dateTimeFullText !== null && this.dateTimeFullText !== undefined) {
        if (Array.isArray(this.dateTimeFullText) && this.dateTimeFullText.length > 1) {
          const sorted = [...this.dateTimeFullText].sort((a, b) => new Date(a) - new Date(b));
          return `${sorted[0]}~${sorted[sorted.length - 1]}`;
        }
        return this.dateTimeFullText;
      }
      return "";
    },
    enums() {
      return enums;
    },
    primaryItems() {
      return (this.items || []).filter((s) => s?.isPrimaryOfGroup);
    },
    speciesBuckets() {
      return speciesHelper.SPECIES_BUCKETS.map((bucket) => ({
        ...bucket,
        count: bucket.codes.reduce((sum, code) => sum + this.countSpecies(code), 0),
      }));
    },
    filteredItems() {
      let list = this.primaryItems;

      if (this.selectedSightingTypes?.length > 0) {
        list = list.filter((s) => this.selectedSightingTypes.includes(s.sightingType));
      }

      if (this.selectedBehaviours?.length > 0) {
        list = list.filter((s) => {
          const bhvs = typeof s.bhv === "string" ? s.bhv.split(",").map((b) => b.trim()) : (s.bhv || []);
          return this.selectedBehaviours.some((b) => bhvs.includes(b));
        });
      }

      return list;
    },
    yearChart() {
      const counts = {};
      this.filteredItems.forEach((s) => {
        const year = s.sightingYear ?? (s.sightingTime ? new Date(s.sightingTime).getFullYear() : null);
        if (year == null) return;
        counts[year] = (counts[year] || 0) + 1;
      });
      const sortedYears = Object.keys(counts).sort();
      const max = Math.max(1, ...Object.values(counts));
      return sortedYears.map((year) => ({
        year,
        count: counts[year],
        pct: (counts[year] / max) * 100,
      }));
    },
  },
  watch: {
    tab(value) {
      if (value === "map" && this.map) {
        this.$nextTick(() => this.map.resize());
      }
    },
    async dateTimeFullText() {
      await this.executeSearch();
    },
    async selectedSpecies() {
      await this.executeSearch();
    },
    async selectedBehaviours() {
      this.reloadPins();
    },
    async selectedSightingTypes() {
      this.reloadPins();
    },
  },
  mounted() {
    setTimeout(() => this.createMap(), 100);
  },
  async created() {
    this.options = this.searchOptions;
    this.query = this.searchQuery;

    if (this.searchDate != null) this.dateTimeFullText = this.searchDate;

    if (this.searchSpecies != null) {
      this.selectedSpecies = this.searchSpecies;
    }

    if (this.searchBehaviour != null) {
      this.selectedBehaviours = this.searchBehaviour;
    }

    if (this.searchSightingType != null) {
      this.selectedSightingTypes = this.searchSightingType;
    }

    await this.executeSearch();
  },
  methods: {
    ...mapActions({
      search: "dashboard/search",
      open: "sighting/details/open",
    }),
    createMap() {
      try {
        mapboxgl.accessToken = this.accessToken;
        this.map = new mapboxgl.Map({
          container: "dashboard-map",
          style: this.mapStyle,
          center: this.mapCenter,
          zoom: this.zoom,
          attributionControl: false,
        });

        this.map.addControl(new mapboxgl.NavigationControl());
        this.map.addControl(new mapboxgl.FullscreenControl());

        this.map.on("load", () => this.reloadPins());
      } catch (err) {
        console.log("map error", err);
      }
    },
    countSpecies(spec) {
      return this.filteredItems.filter((s) => s.species === spec).length;
    },
    toggleBucket(bucket) {
      if (bucket.codes.length <= 1) {
        return;
      }
      this.expandedBuckets = {
        ...this.expandedBuckets,
        [bucket.label]: !this.expandedBuckets[bucket.label],
      };
    },
    async executeSearch() {
      await this.search({
        ...this.options,
        query: this.query,
        date: this.dateTimeFullText,
        species: this.selectedSpecies,
        behaviour: this.selectedBehaviours,
        sightingType: this.selectedSightingTypes,
      });
      this.reloadPins();
    },
    reloadPins() {
      if (!this.map) return;

      this.markers.forEach((m) => m.remove());
      this.markers = [];

      for (let i = 0; i < this.filteredItems?.length; i++) {
        const item = this.filteredItems[i];
        if (item?.lat == null || item?.lon == null) continue;

        const color = speciesHelper.speciesColor(item.species);

        const popupNode = document.createElement("div");
        popupNode.innerHTML = `
          <div>Sighting: ${item.sightingNumber ?? ""}</div>
          <div>Species: ${item.species ?? ""}</div>
          <div>Time: ${item.sightingTime ?? ""}</div>
        `;

        const seeMoreButton = document.createElement("button");
        seeMoreButton.textContent = "See More";
        seeMoreButton.addEventListener("click", () => this.openSightingView(item));
        popupNode.appendChild(seeMoreButton);

        const popup = new mapboxgl.Popup({ closeButton: true, closeOnClick: true })
          .setDOMContent(popupNode);

        const marker = new mapboxgl.Marker({ color, anchor: "bottom" })
          .setLngLat([item.lon, item.lat])
          .setPopup(popup)
          .addTo(this.map);

        this.markers.push(marker);
      }
    },
    openSightingView(item) {
      this.open({ sighting: item });
    },
  },
};
</script>

<style>
.map-class {
  margin-top: 1rem;
  min-height: 400px;
  height: 67vh;
  width: 100%;
}
.filter-boxes {
  margin-top: -7rem;
}
.filter-boxes-2 {
  margin-top: -1.5rem;
}
.year-chart {
  padding: 0.5rem 0;
}
.year-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
  font-size: 0.85em;
}
.year-label {
  width: 3.5rem;
  flex-shrink: 0;
}
.year-bar-wrap {
  flex-grow: 1;
  background-color: #f1f5f9;
  height: 1rem;
  border-radius: 2px;
  overflow: hidden;
}
.year-bar {
  height: 100%;
  background-color: rgb(var(--v-theme-primary));
  transition: width 0.2s;
}
.year-count {
  width: 2.5rem;
  flex-shrink: 0;
  text-align: right;
}
.bucket-row {
  cursor: pointer;
}
.bucket-child-row {
  background-color: #f8fafc;
}
</style>
